import { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Chip,
  Stack,
} from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CategoryIcon from "@mui/icons-material/Category";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LayersIcon from "@mui/icons-material/Layers";
import ShieldIcon from "@mui/icons-material/Shield";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchEntries } from "@/store/entriesSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { useExpenseSummary } from "@hooks/useExpenseSummary";
import { BRAND, STAT_ACCENTS } from "@/constants/brand";
import { PageContainer } from "@components/PageContainer";
import { StatCard } from "@components/StatCard";

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const { items: entries } = useAppSelector((s) => s.entries);
  const { items: categories } = useAppSelector((s) => s.categories);

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(fetchEntries());
    dispatch(fetchCategories());
  }, [dispatch, isAuthenticated]);

  const {
    totalExpenses,
    totalGains,
    net,
    entryCount,
    categoryCount,
    subcategoryCount,
  } = useExpenseSummary(entries, categories);

  const statCards = useMemo(
    () => [
      {
        icon: <TrendingDownIcon />,
        label: t("home.totalExpenses"),
        value: `${totalExpenses.toFixed(2)} EUR`,
        accentColor: STAT_ACCENTS.expenses.color,
      },
      {
        icon: <TrendingUpIcon />,
        label: t("home.totalGains"),
        value: `${totalGains.toFixed(2)} EUR`,
        accentColor: STAT_ACCENTS.gains.color,
      },
      {
        icon: <AccountBalanceWalletIcon />,
        label: t("home.netBalance"),
        value: `${net.toFixed(2)} EUR`,
        accentColor: STAT_ACCENTS.net.color,
      },
      {
        icon: <ReceiptIcon />,
        label: t("home.totalEntries"),
        value: String(entryCount),
        accentColor: STAT_ACCENTS.entries.color,
      },
      {
        icon: <CategoryIcon />,
        label: t("home.categories"),
        value: String(categoryCount),
        accentColor: STAT_ACCENTS.categories.color,
      },
      {
        icon: <SubdirectoryArrowRightIcon />,
        label: t("home.subcategories"),
        value: String(subcategoryCount),
        accentColor: STAT_ACCENTS.categories.color,
      },
    ],
    [
      t,
      totalExpenses,
      totalGains,
      net,
      entryCount,
      categoryCount,
      subcategoryCount,
    ],
  );

  const features = useMemo(
    () => [
      {
        icon: <ShowChartIcon />,
        title: t("home.features.insights"),
        desc: t("home.features.insightsDesc"),
        color: BRAND.peachTint,
        iconColor: BRAND.orange,
      },
      {
        icon: <LayersIcon />,
        title: t("home.features.categories"),
        desc: t("home.features.categoriesDesc"),
        color: "#DBEAFE",
        iconColor: "#3B82F6",
      },
      {
        icon: <ShieldIcon />,
        title: t("home.features.secure"),
        desc: t("home.features.secureDesc"),
        color: "#FEE2E2",
        iconColor: BRAND.danger,
      },
      {
        icon: <PhoneIphoneIcon />,
        title: t("home.features.mobile"),
        desc: t("home.features.mobileDesc"),
        color: "#F3E8FF",
        iconColor: "#8B5CF6",
      },
    ],
    [t],
  );

  return (
    <PageContainer>
      <Box
        sx={(theme) => ({
          ...(theme.palette.mode === "dark"
            ? {
                background: `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.22)} 0%, ${theme.palette.background.paper} 55%, ${alpha(theme.palette.background.default, 0.98)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.22)}`,
              }
            : { bgcolor: BRAND.peachTint }),
          borderRadius: "24px",
          p: { xs: 4, lg: 6 },
          mb: 6,
          position: "relative",
          overflow: "hidden",
        })}
      >
        <Chip
          label={t("home.badge")}
          sx={(theme) => ({
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.1)
                : "white",
            color: "secondary.main",
            fontWeight: "bold",
            fontSize: 12,
            mb: 2,
            textTransform: "uppercase",
            letterSpacing: 1,
          })}
        />
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            fontFamily: "'Playfair Display', serif",
            mb: 2,
            maxWidth: 500,
          }}
        >
          {t("home.title")}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 4,
            maxWidth: 500,
            fontSize: 18,
          }}
        >
          {t("home.subtitle")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate(isAuthenticated ? "/tracker" : "/account")}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: 16,
            }}
          >
            {t("home.startTracking")}
          </Button>
          {!isAuthenticated && (
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              component={RouterLink}
              to="/preview"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: 16,
              }}
            >
              {t("preview.cta")}
            </Button>
          )}
        </Stack>
      </Box>
      {isAuthenticated && (
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {statCards.map((stat) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              key={stat.label}
              sx={{ display: "flex" }}
            >
              <StatCard
                icon={stat.icon}
                label={stat.label}
                value={stat.value}
                accentColor={stat.accentColor}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontFamily: "'Playfair Display', serif",
            mb: 1,
          }}
        >
          {t("home.features.title")}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          {t("home.features.subtitle")}
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid size={{ xs: 12, md: 6, lg: 3 }} key={feature.title}>
              <Card elevation={0} sx={{ height: "100%", p: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    bgcolor: feature.color,
                    color: feature.iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    fontSize: 18,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.7,
                  }}
                >
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </PageContainer>
  );
}
